'use client';

import classNames from 'classnames';
import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { Button } from 'src/general/components/Button/Button';
import { HighlightedFeatureCarousel } from 'src/pdp/components/HighlightedFeatureCarousel/HighlightedFeatureCarousel';
import { formatFeatures } from 'src/pdp/sections/Pdp/pdpHelpers';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import styles from './Features.module.scss';

const MAX_FEATURES_ITEM = 6;

type Props = {
  productDetails: ProductDetailsPayload;
};

export const Features: FC<Props> = ({ productDetails }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );

  const { detailedHighlightedFeatures, carFeatures } = formatFeatures(productDetails);

  const popularFeaturesCount = detailedHighlightedFeatures.length;
  const optionalFeaturesCount = carFeatures.optionalFeatures.length;
  const aftermarketFeaturesCount = carFeatures.aftermarketFeatures.length;
  const standardFeaturesCount = carFeatures.standardFeatures.length;
  const hasAdditionalFeatures = !!(optionalFeaturesCount || aftermarketFeaturesCount || standardFeaturesCount);

  const numOfFeatures = aftermarketFeaturesCount + optionalFeaturesCount + standardFeaturesCount;

  const handleOpenModal = () => {
    setCurrentModal({ name: 'features', initialTab: 'features' });
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'modal',
      value: 'view_all_features',
    });
  };

  if (numOfFeatures === 0) {
    return null;
  }

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <h3 className={classNames(styles.title, 'c-fw-bold', 'c-fs-h5')}>Features we think you’ll love</h3>
      </header>
      {!!popularFeaturesCount && (
        <div className={styles.carouselContainer} data-testid={`feature_popular_features`}>
          <h4 className={styles.subTitle}>Popular features</h4>
          <HighlightedFeatureCarousel features={detailedHighlightedFeatures} />
        </div>
      )}
      {hasAdditionalFeatures && (
        <dl className={styles.descList}>
          <FeatureCategory
            title="Installed options"
            count={optionalFeaturesCount}
            items={carFeatures.optionalFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
          <FeatureCategory
            title="Aftermarket accessories"
            count={aftermarketFeaturesCount}
            items={carFeatures.aftermarketFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
          <FeatureCategory
            title="Standard features"
            count={standardFeaturesCount}
            items={carFeatures.standardFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
        </dl>
      )}
      <footer className={styles.footer}>
        <Button
          variant="secondary"
          size="small"
          onClick={handleOpenModal}
          className={styles.cta}
          data-testid="Features-viewAll"
        >
          View all {numOfFeatures} features
        </Button>
      </footer>
    </section>
  );
};

interface FeatureCategoryProps {
  title: string;
  count: number;
  items: string[];
}

const FeatureCategory: FC<FeatureCategoryProps> = ({ title, count, items }) =>
  count ? (
    <div>
      <dt className={styles.descTitle} data-testid={`feature_${title.replace(' ', '_')}`}>
        {title} <span data-testid={`feature_${title.replace(' ', '_')}_count`}>({count})</span>
      </dt>
      <div className={styles.descContent}>
        {items.map((item) => (
          <dd
            key={item}
            className={classNames(styles.descDefinition, 'c-fw-normal', 'c-fs-body2')}
            data-testid={`feature_${title.replace(' ', '_')}_items`}
          >
            {item}
          </dd>
        ))}
      </div>
    </div>
  ) : null;
